<template>
  <v-dialog
      v-model="visible"
      hide-overlay
      persistent
      width="auto"
  >
    <v-card width="600">
      <div class="px-6 pt-4 d-flex align-items-center">
        <div class="pt-2 font-14 font-weight-medium">
            Variação permitida na quantidade desejada
        </div>

        <v-spacer />

        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="mx-6 text-body-2 text--secondary mb-4">
        Defina quanto os fornecedores podem reduzir ou aumentar a quantidade que você
        pediu, para se adequar mínimos e múltiplos de venda.
      </div>

      <div class="mx-6 my-2">
        <v-sheet
            rounded
            class="pa-4 mb-6"
            color="#f8fafc"
            style="border: 1px solid #e2e8f0;"
        >
          <!-- Ícone + título -->
          <div class="d-flex align-center mb-2">
            <v-icon color="#3b82f6" class="mr-2">mdi-information-outline</v-icon>
            <span class="font-weight-medium">Exemplo de aplicação</span>
          </div>

          <!-- Texto do exemplo -->
          <div class="text-body-2 text--secondary">
            Com base na Qtd pedida, vamos recalcular para cada item:
            Qtd mín. aceitável = Qtd pedida – X%
            <br>
            Qtd máx. aceitável = Qtd pedida + Y%
            <br><br>
            Exemplo: Se você deseja 945 unidades, e configura uma tolerância
            de 10% para mais e para menos, ele poderá ofertar entre
            <strong>851 unidades e 1039 unidades</strong>.
          </div>
        </v-sheet>

        <div class="mt-8">
          <div class="text-body-2 text--secondary">
            <div class="d-flex align-items-center">
              <span class="mt-2">Aceito receber até</span>
              <InputNumber
                v-model="toleranciaForm.minima"
                center
                class="input-regular font-resp txt-table-prod"
              />
              <span class="mt-2"><strong>% a menos</strong> que o desejado.</span>
            </div>
            <p class="font-11">Percentual que você permite arredondar para baixo. Use 0% se não quiser receber menos do que pediu.</p>
            <p v-if="showMinimaWarning" class="font-11 red--text mt-1">Use valores entre 0% e 100%.</p>
          </div>
          <div class="text-body-2 text--secondary mt-8">
            <div class="d-flex align-items-center">
              <span class="mt-2">Aceito receber até</span>
              <InputNumber
                v-model="toleranciaForm.maxima"
                center
                class="input-regular font-resp txt-table-prod"
              />
              <span class="mt-2"><strong>% a mais</strong> que o desejado.</span>
            </div>
            <p class="font-11">Percentual que você permite arredondar para cima. Use 0% se não quiser receber mais do que pediu.</p>
            <p v-if="showMaximaWarning" class="font-11 red--text mt-1">Use valores entre 0% e 100%.</p>
          </div>
        </div>

        <div>
          <span class="font-weight-medium">Isso será aplicado a</span>

          <div class="mt-6">
            <v-radio-group
                v-model="toleranciaForm.applyAll"
                mandatory
            >
              <v-radio
                  class="mb-4"
                  label="Todos os itens da cotação"
                  :value="true"
              ></v-radio>

              <SysTooltip
                  :disabled="canApplyOnly"
                  bottom
                  title="Somente itens selecionados"
                  text="Nenhum item selecionado. Selecione itens na grade para usar esta opção."
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs" style="display: inline-block; width: 100%;">
                    <v-radio
                        :disabled="!canApplyOnly"
                        :value="false"
                    >
                      <template v-slot:label>
                        Somente <strong v-if="currentSelectedCount > 0" class="mx-1">{{ currentSelectedCount }}</strong> itens selecionados
                      </template>
                    </v-radio>
                  </div>
                </template>
              </SysTooltip>
            </v-radio-group>
          </div>

          <p class="font-11 text--secondary">Os itens que estiverem fora do escopo escolhido não terão sua tolerância alterada.</p>
        </div>
      </div>

      <v-card-title class="d-flex justify-space-between pb-6">
        <div>
          <v-btn class="wc-btn-actions" color="primary" outlined @click="resetarFormulario">
            Resetar valores
          </v-btn>
        </div>
        <div class="d-flex">
          <v-btn class="mx-2 wc-btn-actions" text @click="close">
            Cancelar
          </v-btn>

          <v-btn class="wc-btn-actions" color="primary" @click="handleAplyTolerancia">
            Aplicar tolerância
          </v-btn>
        </div>
      </v-card-title>
    </v-card>
  </v-dialog>
</template>

<script setup>
import SysTooltip from "@/components/utils/ui_v4/tooltips/SysTooltip.vue";
import InputNumber from "@/components/webcotacao/components/InputNumber.vue";
import usePercentToleranciaDialog from "@/components/webcotacao/composables/usePercentToleranciaDialog";
import { defineEmits, defineProps } from "vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  handleClose: {
    type: Function,
    required: true,
  },
  isAllSelected: {
    type: Boolean,
    default: false,
  },
  selectedCount: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(["apply-tolerancia"]);

const {
  toleranciaForm,
  showMinimaWarning,
  showMaximaWarning,
  close,
  handleAplyTolerancia,
  resetarFormulario,
  canApplyOnly,
  currentSelectedCount
} = usePercentToleranciaDialog(props, emit);
</script>

<style>

.input-regular {
  padding: 0 !important;
}
.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}
.txt-table-prod {
  padding: 0;
  max-width: 100px;
  min-width: 100px;
  margin: 0;
  margin-top: 2px;
}
.txt-table-prod .v-input__control .v-input__slot * {
  text-align: center;
  margin: 0;
  padding: 0;
  padding-left: 3px;
}
</style>
